import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';

const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const Admin2FASetup = lazy(() => import('./pages/admin/Admin2FASetup'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminJobs = lazy(() => import('./pages/admin/AdminJobs'));
const AdminPayments = lazy(() => import('./pages/admin/AdminPayments'));
const AdminConfig = lazy(() => import('./pages/admin/AdminConfig'));
const AdminModeration = lazy(() => import('./pages/admin/AdminModeration'));
const AdminEquipmentVerifications = lazy(() => import('./pages/admin/AdminEquipmentVerifications'));
const AdminCancellationRequests = lazy(() => import('./pages/admin/AdminCancellationRequests'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminFinancialReport = lazy(() => import('./pages/admin/AdminFinancialReport'));
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));
const AdminCoverage = lazy(() => import('./pages/admin/AdminCoverage'));
const AdminPlatformMonitor = lazy(() => import('./pages/admin/AdminPlatformMonitor'));
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminStaff = lazy(() => import('./pages/admin/AdminStaff'));
const AdminPermissionRoute = lazy(() => import('./components/admin/AdminPermissionRoute'));
const RefLanding = lazy(() => import('./pages/RefLanding'));

const LandingPage = lazy(() => import('./pages/LandingPage'));
const VideomakerLandingPage = lazy(() => import('./pages/VideomakerLandingPage'));
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import CompletarPerfil from './pages/CompletarPerfil';
import RecuperarSenha from './pages/RecuperarSenha';
import ConfirmarEmail from './pages/ConfirmarEmail';
import NotFoundPublic from './pages/NotFoundPublic';
import TermosDeUso from './pages/TermosDeUso';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import PoliticaCancelamentoReembolso from './pages/PoliticaCancelamentoReembolso';
import PoliticaRepassesVideomaker from './pages/PoliticaRepassesVideomaker';
import CentralAjuda from './pages/CentralAjuda';
import PublicLayout from './components/PublicLayout';
import GoogleOAuthProviderWrapper from './components/GoogleOAuthProviderWrapper';
import EscolherPerfilSocial from './pages/EscolherPerfilSocial';

import StripeConnectReturn from './pages/StripeConnectReturn';
import StripeConnectRefresh from './pages/StripeConnectRefresh';

import RoleRoute from './components/auth/RoleRoute';
import UserAppShell from './components/user/UserAppShell';
import CookieConsentBanner from './components/CookieConsentBanner';
import ClienteJobsPage from './pages/cliente/ClienteJobsPage';
import ClienteDeliveryPage from './pages/cliente/ClienteDeliveryPage';
import ClienteProfilePage from './pages/cliente/ClienteProfilePage';
import VideomakerJobsPage from './pages/videomaker/VideomakerJobsPage';
import VideomakerDeliveryUploadPage from './pages/videomaker/VideomakerDeliveryUploadPage';
import VideomakerFinancePage from './pages/videomaker/VideomakerFinancePage';
import VideomakerProfilePage from './pages/videomaker/VideomakerProfilePage';

const adminFallback = <div className="min-h-screen bg-[#0F1629]" />;

function App() {
  return (
    <ThemeProvider>
      <GoogleOAuthProviderWrapper>
        <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <CookieConsentBanner />
          <Routes>
            <Route
              path="/"
              element={(
                <Suspense fallback={<div className="min-h-screen bg-[#0F1629]" />}>
                  <LandingPage />
                </Suspense>
              )}
            />
            <Route
              path="/para-videomakers"
              element={(
                <Suspense fallback={<div className="min-h-screen bg-[#0F1629]" />}>
                  <VideomakerLandingPage />
                </Suspense>
              )}
            />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/cadastro" element={<PublicLayout><Cadastro /></PublicLayout>} />
            <Route path="/completar-perfil" element={<PublicLayout><CompletarPerfil /></PublicLayout>} />
            <Route path="/escolher-perfil" element={<PublicLayout><EscolherPerfilSocial /></PublicLayout>} />
            <Route path="/recuperar-senha" element={<PublicLayout><RecuperarSenha /></PublicLayout>} />
            <Route path="/confirmar-email" element={<PublicLayout><ConfirmarEmail /></PublicLayout>} />
            <Route path="/termos-de-uso" element={<PublicLayout><TermosDeUso /></PublicLayout>} />
            <Route path="/politica-de-privacidade" element={<PublicLayout><PoliticaPrivacidade /></PublicLayout>} />
            <Route path="/politica-cancelamento-reembolso" element={<PublicLayout><PoliticaCancelamentoReembolso /></PublicLayout>} />
            <Route path="/politica-repasses-videomaker" element={<PublicLayout><PoliticaRepassesVideomaker /></PublicLayout>} />
            <Route
              path="/ref/:code"
              element={(
                <Suspense fallback={adminFallback}>
                  <RefLanding />
                </Suspense>
              )}
            />
            <Route
              path="/invite/:code"
              element={(
                <Suspense fallback={adminFallback}>
                  <RefLanding />
                </Suspense>
              )}
            />
            <Route path="/ajuda" element={<PublicLayout><CentralAjuda /></PublicLayout>} />
            <Route path="/central-de-ajuda" element={<PublicLayout><CentralAjuda /></PublicLayout>} />

            <Route path="/cliente/dashboard" element={<Navigate to="/cliente/jobs" replace />} />
            <Route path="/videomaker/dashboard" element={<Navigate to="/videomaker/jobs" replace />} />

            <Route
              path="/cliente"
              element={
                <RoleRoute role="client">
                  <UserAppShell role="client" />
                </RoleRoute>
              }
            >
              <Route index element={<Navigate to="/cliente/jobs" replace />} />
              <Route path="jobs" element={<ClienteJobsPage />} />
              <Route path="jobs/:jobId" element={<ClienteDeliveryPage />} />
              <Route path="perfil" element={<ClienteProfilePage />} />
            </Route>

            <Route
              path="/videomaker"
              element={
                <RoleRoute role="videomaker">
                  <UserAppShell role="videomaker" />
                </RoleRoute>
              }
            >
              <Route index element={<Navigate to="/videomaker/jobs" replace />} />
              <Route path="jobs" element={<VideomakerJobsPage />} />
              <Route path="jobs/:jobId" element={<VideomakerDeliveryUploadPage />} />
              <Route path="financeiro" element={<VideomakerFinancePage />} />
              <Route path="perfil" element={<VideomakerProfilePage />} />
            </Route>

            <Route path="/stripe-connect/return" element={<StripeConnectReturn />} />
            <Route path="/stripe-connect/refresh" element={<StripeConnectRefresh />} />

            <Route
              path="/admin/login"
              element={(
                <Suspense fallback={adminFallback}>
                  <AdminLogin />
                </Suspense>
              )}
            />
            <Route
              path="/admin"
              element={(
                <Suspense fallback={adminFallback}>
                  <AdminLayout />
                </Suspense>
              )}
            >
              <Route path="2fa-setup" element={<Suspense fallback={adminFallback}><Admin2FASetup /></Suspense>} />
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminDashboard /></AdminPermissionRoute></Suspense>} />
              <Route path="platform-monitor" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminPlatformMonitor /></AdminPermissionRoute></Suspense>} />
              <Route path="analytics" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminAnalytics /></AdminPermissionRoute></Suspense>} />
              <Route path="users" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminUsers /></AdminPermissionRoute></Suspense>} />
              <Route path="jobs" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminJobs /></AdminPermissionRoute></Suspense>} />
              <Route path="payments" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminPayments /></AdminPermissionRoute></Suspense>} />
              <Route path="coupons" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminCoupons /></AdminPermissionRoute></Suspense>} />
              <Route path="referrals" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminReferrals /></AdminPermissionRoute></Suspense>} />
              <Route path="financial-report" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminFinancialReport /></AdminPermissionRoute></Suspense>} />
              <Route path="notifications" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminNotifications /></AdminPermissionRoute></Suspense>} />
              <Route path="pricing" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminPricing /></AdminPermissionRoute></Suspense>} />
              <Route path="coverage" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminCoverage /></AdminPermissionRoute></Suspense>} />
              <Route path="config" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminConfig /></AdminPermissionRoute></Suspense>} />
              <Route path="staff" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminStaff /></AdminPermissionRoute></Suspense>} />
              <Route path="moderation" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminModeration /></AdminPermissionRoute></Suspense>} />
              <Route path="equipment-verifications" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminEquipmentVerifications /></AdminPermissionRoute></Suspense>} />
              <Route path="cancellation-requests" element={<Suspense fallback={adminFallback}><AdminPermissionRoute><AdminCancellationRequests /></AdminPermissionRoute></Suspense>} />
            </Route>

            <Route path="*" element={<PublicLayout><NotFoundPublic /></PublicLayout>} />
          </Routes>
        </BrowserRouter>
        </AuthProvider>
      </GoogleOAuthProviderWrapper>
    </ThemeProvider>
  );
}

export default App;
